import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';

import { TicketStatus } from '@hrms/shared/types';

import './DashboardLayout.scss';
import { ticketsApi } from '../../api/tickets.api';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { useAuth } from '../../context/AuthContext';
import { BannerPortalContext } from '../../context/BannerPortalContext';
import { mountBannerParticles } from '../banner/BannerParticles';
import { clearDismissedNotifications, dismissNotification, mountNavbar, updateBreadcrumb, updateNotifications } from '../navbar/PeopleSuiteNavbar';
import { mountSidebar, setActiveNavByPath, updateSidebarGroupOwner, updateSidebarApprover, updateSidebarRole } from '../sidebar/PeopleSuiteSidebar';

import { groupsApi } from '@/api/groups.api';
import { leaveApi } from '@/api/leave.api';
import { socketService } from '../../services/socket.service';

/**
 * Persistent app shell — mounted ONCE at the route level (see AppRoutes.tsx), wrapping
 * an <Outlet/> for page content. Do not render this per-page: the sidebar/navbar are
 * imperative vanilla-JS mounts that must survive navigation, not remount on every route
 * change. Pages that need custom banner content should use the `useBannerContent` hook
 * instead of a prop, since it portals into the persistent banner without forcing this
 * component to re-render.
 */
export const DashboardLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [bannerTarget, setBannerTarget] = useState<HTMLDivElement | null>(null);

  const navRef = useRef<HTMLDivElement>(null);
  const navCleanupRef = useRef<(() => void) | null>(null);
  const sidebarCleanupRef = useRef<(() => void) | null>(null);
  const effectiveBannerRef = useRef<HTMLDivElement>(null);
  const bannerCleanupRef = useRef<(() => void) | null>(null);

  // useCallback (keyed only on location.pathname, its sole input) so the route-change
  // effect below can depend on it directly instead of disabling exhaustive-deps.
  const getPageName = useCallback((): string => {
    const path = location.pathname.replace(/^\//, '');
    const segment = path.split('/')[0];
    if (!segment) return 'Dashboard';
    const labelMap: Record<string, string> = {
      'pre-onboarding': 'Pre-Onboarding',
      users: 'User Management',
      groups: 'Group Management',
      tickets: 'Tickets',
      attendance: 'Attendance',
      reports: 'Reports',
      settings: 'Settings',
      payroll: 'Payroll',
      dashboard: 'Dashboard',
    };
    return labelMap[segment] ?? (segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '));
  }, [location.pathname]);

  /* Mount navbar — intentional [] (imperative mount, single run) */
  useEffect(() => {
    if (!navRef.current) return;
    navCleanupRef.current = mountNavbar(navRef.current, {
      getPageName,
      userName: user?.fullName || user?.firstName || '',
      userEmail: user?.email || '',
      userRole: user?.role || '',
      onNavigate: (path: string) => navigate(path),
      onSignOut: () => {
        clearDismissedNotifications();
        // Navigate to /login even if logout() rejects — otherwise a failed
        // sign-out silently leaves the user sitting on an authenticated page.
        void logout()
          .catch((err) => console.error('Logout failed; signing out locally', err))
          .finally(() => navigate('/login'));
      },
    });
    return () => { if (navCleanupRef.current) navCleanupRef.current(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Fetch real notifications for logged-in user with periodic refresh */
  const fetchNotifications = useCallback(() => {
    if (!user?._id) return;
    const items: Parameters<typeof updateNotifications>[0] = [];

    const pending = leaveApi.getPendingApprovals({ page: 1, perPage: 1 })
      .then(res => {
        const count = res?.data?.pagination?.total ?? 0;
        if (count > 0) {
          items.push({
            id: 'leave-approval',
            msg: `${count} pending leave approval${count > 1 ? 's' : ''}`,
            time: 'Review approvals',
            link: '/attendance/leaves/approvals',
            color: '#C9B037',
            count,
          });
        }
      }).catch(() => {});

    const tickets = ticketsApi.getTickets({
      myTickets: true,
      status: [TicketStatus.OPEN, TicketStatus.IN_PROGRESS, TicketStatus.REOPENED],
      limit: 1,
    })
      .then(res => {
        const count = res?.pagination?.total ?? 0;
        if (count > 0) items.push({
          id: 'ticket-assigned',
          msg: `${count} ticket${count > 1 ? 's' : ''} assigned to you`,
          time: 'Open tickets',
          link: '/tickets',
          color: '#2B9BA5',
          count,
        });
      }).catch(() => {});

    void Promise.allSettled([pending, tickets]).then(() => {
      updateNotifications(items);
    });
  }, [user?._id]);

  useEffect(() => {
    fetchNotifications();

    // Real-time socket event updates
    socketService.on('notification', fetchNotifications);
    socketService.on('ticket:assigned', fetchNotifications);
    socketService.on('ticket:updated', fetchNotifications);

    return () => {
      socketService.off('notification', fetchNotifications);
      socketService.off('ticket:assigned', fetchNotifications);
      socketService.off('ticket:updated', fetchNotifications);
    };
  }, [fetchNotifications, location.pathname]);

  /* Mount sidebar — intentional [] (imperative mount, single run) */
  useEffect(() => {
    sidebarCleanupRef.current = mountSidebar({
      onNavigate: (path: string) => navigate(path),
      initialPath: location.pathname,
      role: user?.role,
      department: user?.department,
    });
    return () => { if (sidebarCleanupRef.current) sidebarCleanupRef.current(); };
    // Deliberately NOT adding location.pathname/user?.*/navigate: this is a one-time
    // imperative mount. `initialPath` is captured once by design — live route updates go
    // through setActiveNavByPath() in the route-change effect below instead, so adding
    // location.pathname here would remount the sidebar (and its DOM/animation state) on
    // every navigation, not just update its active item.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Sync sidebar role and department when user object updates */
  useEffect(() => {
    if (user?.role) {
      updateSidebarRole(user.role, user.department);
    }
  }, [user?.role, user?.department]);

  /* Detect group ownership and reveal "Employees" sidebar item for group owners */
  useEffect(() => {
    if (!user?._id) return;
    groupsApi.getGroups()
      .then(res => {
        const ownsGroup = res.groups.some(
          g => typeof g.owner === 'object' && g.owner !== null && g.owner._id === user._id
        );
        updateSidebarGroupOwner(ownsGroup);
      })
      .catch(() => {});

    import('../../api/users.api').then(({ usersApi }) => {
      // Check if user is HR for anyone
      usersApi.getUsers({ hr: user._id, limit: 1 }).then(hrRes => {
        if (hrRes.totalCount > 0) {
          updateSidebarApprover(true);
        } else {
          // If not HR, check if they are a reporting manager for anyone
          usersApi.getUsers({ reportingManager: user._id, limit: 1 }).then(rmRes => {
            updateSidebarApprover(rmRes.totalCount > 0);
          }).catch(() => {});
        }
      }).catch(() => {});
    });
  }, [user?._id]);

  /* Mount banner particles */
  useEffect(() => {
    if (!effectiveBannerRef.current) return;
    bannerCleanupRef.current = mountBannerParticles(effectiveBannerRef.current);
    return () => { if (bannerCleanupRef.current) bannerCleanupRef.current(); };
  }, []);


  /* Update breadcrumb + sidebar on route change */
  useEffect(() => {
    updateBreadcrumb(getPageName());
    setActiveNavByPath(location.pathname);
  }, [getPageName, location.pathname]);

  return (
    <>
      <div className="db-wrap">
        {/* Navbar mount point */}
        <div ref={navRef} />

        {/* Banner (always rendered; particles mount into it; content portaled in per-page) */}
        <main className="db-main">
          <div className="db-inner">
            <div className="db-banner" ref={effectiveBannerRef}>
              <div className="db-banner-sweep" />
              <div className="db-banner-content" ref={setBannerTarget} />
            </div>
            <BannerPortalContext.Provider value={bannerTarget}>
              <Suspense fallback={<LoadingSpinner />}>
                <Outlet />
              </Suspense>
            </BannerPortalContext.Provider>
          </div>
        </main>
      </div>
    </>
  );
};
